import { React, useState, useEffect, useRef } from 'react'
import { Box, CircularProgress, Typography } from '@mui/material'
import AdxInput from 'components/custom/AdxInput'
import { InputAdornment } from '@mui/material'
import AdxDropdown from 'components/custom/AdxDropdown'
import AdxMenuItem from 'components/custom/AdxMenuItem'
import { Formik, Form, ErrorMessage } from 'formik'
import * as yup from 'yup'
import { useMutation, useQueryClient } from 'react-query'
import { handleValidationErrors, handleScroll } from 'services/helper/handleValidationErrors'

// API
import { postBankData } from 'services/api/modules/staff-management/staffManagementDashboard'
import AdxErrorMessage from 'components/custom/AdxErrorMessage'
import { recalculateSelectedEmployee } from 'services/api/modules/payroll/payroll-view'
import Bugsnag from '@bugsnag/js'
import AdxButton from 'components/custom/AdxButton'

const validationSchema = yup.object({
  salary_category: yup.string().nullable().required('Salary Category is required'),
  basic_salary: yup
    .string()
    .nullable()
    .required('Basic Salary is required')
    .matches(/^[0-9]{1,11}(?:\.[0-9]{1,2})?$/, 'Salary can only contain numbers'),
  bank_id: yup.string().nullable().required('Bank Name is required'),
  bank_acc_number: yup
    .string()
    .nullable()
    .required('Bank Account Number is required')
    .matches(/^[0-9\b]+$/, 'Bank Account Number can only contain numbers'),
})

const SalaryDetails = ({ employeeId, time_id, data, salCat, bankDropdown, empType }) => {
  const queryClient = useQueryClient()
  const formikRef = useRef()
  const [isLoading, setIsLoading] = useState(false)

  const [disable, setDisable] = useState(true)
  const [initialValues, setInitialValues] = useState({
    salary_category: '',
    basic_salary: '',
    bank_id: '',
    bank_acc_number: '',
  })
  const cancelEditForm = (resetForm) => {
    resetForm()
    setDisable(true)
  }

  // RECALCULATE
  const { mutate: recalculatePayroll } = useMutation(recalculateSelectedEmployee, {
    onSuccess: (res) => {
      setIsLoading(false)
      setDisable(true)
      queryClient.invalidateQueries('retrieveEmployeeData')
      queryClient.invalidateQueries('employeeBankData')
    },
    onError: (error) => {
      if (error.status === 422) {
        handleValidationErrors(error, formikRef)
        console.log(error)
        if (error.data.errors) {
          handleScroll(error.data.errors)
        }
      } else {
        Bugsnag.notify(error.data)
      }
    },
  })

  const mutation = useMutation((value) => postBankData(employeeId, value), {
    onSuccess: (response) => {
      setIsLoading(true)
      recalculatePayroll({ time_id, employee_id: employeeId, data: {} })
    },
    onError: (error) => {
      if (error.status === 422) {
        handleValidationErrors(error, formikRef)
        console.log(error)
        if (error.data.errors) {
          handleScroll(error.data.errors)
        }
      } else {
        Bugsnag.notify(error.data)
      }
    },
  })

  const handleSubmitSalary = (values, action) => {
    mutation.mutate(values)
  }

  useEffect(() => {
    if (data) {
      setInitialValues({
        salary_category: data?.data?.salary_category,
        basic_salary: data?.data?.basic_salary ? data?.data?.basic_salary : '',
        bank_id: data?.data?.bank_id,
        bank_acc_number: data?.data?.bank_acc_number ? data?.data?.bank_acc_number : '',
      })
    }
  }, [data])

  const handleCheckRef = async (handleFormikSubmit) => {
    await handleFormikSubmit()
    const { errors = {} } = formikRef.current
    handleScroll(errors)
  }

  return (
    <Formik
      onSubmit={handleSubmitSalary}
      initialValues={initialValues}
      enableReinitialize
      validationSchema={validationSchema}
      innerRef={formikRef}
    >
      {({
        values,
        touched,
        errors,
        handleChange,
        handleSubmit,
        isValid,
        dirty,
        setFieldValue,
        resetForm,
      }) => (
        <Form>
          <Box
            p={1.75}
            display="flex"
            justifyContent="space-between"
            borderBottom="1px solid"
            sx={{ borderColor: 'gray-line.main' }}
            alignItems="center"
          >
            <Typography color="light-gray-text.main" variant="subtitle2" fontWeight={500}>
              Salary details
            </Typography>
            {disable && (
              <Typography
                sx={{
                  textDecoration: 'underline',
                  color: 'alternate.main',
                  cursor: 'pointer',
                }}
                onClick={() => setDisable(false)}
              >
                Edit
              </Typography>
            )}
          </Box>
          <Box p={1.75} sx={{ borderColor: 'gray-line.main' }} mt={2}>
            <Box gap={2.5} display="flex" flexDirection={{ xs: 'column', sm: 'row' }} mb={2.5}>
              <Box width={{ xs: '100%', sm: '30%' }}>
                <AdxDropdown
                  label="Salary category"
                  disabled={disable}
                  sxLabel={{
                    color: disable ? 'light-gray-text.main' : 'dark-text.main',
                  }}
                  sxInput={{
                    color: 'light-gray-text.main',
                    backgroundColor: 'white.main',
                  }}
                  size="standard"
                  value={values.salary_category}
                  name="salary_category"
                  onChange={handleChange}
                  errorMessage={<ErrorMessage name="salary_category" component={AdxErrorMessage} />}
                  isErrorShown={touched.salary_category && errors.salary_category}
                >
                  {salCat &&
                    salCat?.map((item) => (
                      <AdxMenuItem
                        value={item.id}
                        selectedvalue={values.salary_category}
                        disabled={item.id === 0}
                      >
                        {item.description}
                      </AdxMenuItem>
                    ))}
                </AdxDropdown>
              </Box>
              <Box
                width={{ xs: '100%', sm: '60%' }}
                sx={{ display: 'flex', alignItems: 'flex-start' }}
              >
                <AdxInput
                  label="Basic salary"
                  disabled={disable}
                  sxLabel={{
                    color: disable ? 'light-gray-text.main' : 'dark-text.main',
                  }}
                  sxInput={{
                    color: 'light-gray-text.main',
                    backgroundColor: 'white.main',
                  }}
                  size="standard"
                  placeholder="XX,XXX"
                  value={values.basic_salary}
                  name="basic_salary"
                  onChange={handleChange}
                  startAdornment={<InputAdornment position="start">RM</InputAdornment>}
                  sx={{ width: { xs: '70%', sm: '50%' }, mr: 2 }}
                  errorMessage={<ErrorMessage name="basic_salary" component={AdxErrorMessage} />}
                  isErrorShown={touched.basic_salary && errors.basic_salary}
                />
                <Typography sx={{ mt: 5 }} variant="body1" color="light-gray-text.main">
                  {values.salary_category
                    ? values.salary_category === 0
                      ? 'none'
                      : values.salary_category === 1
                      ? 'per hour'
                      : values.salary_category === 2
                      ? 'per day'
                      : values.salary_category === 3
                      ? 'per month'
                      : 'undefined'
                    : ''}
                </Typography>
              </Box>
            </Box>
            <Box gap={2.5} display="flex" flexDirection={{ xs: 'column', sm: 'row' }} mb={2}>
              <Box width={{ xs: '100%', sm: '60%' }}>
                <AdxDropdown
                  label="Bank name"
                  disabled={disable}
                  sxLabel={{
                    color: disable ? 'light-gray-text.main' : 'dark-text.main',
                  }}
                  sxInput={{
                    color: 'light-gray-text.main',
                    backgroundColor: 'white.main',
                  }}
                  size="standard"
                  value={values.bank_id}
                  name="bank_id"
                  onChange={handleChange}
                  errorMessage={<ErrorMessage name="bank_id" component={AdxErrorMessage} />}
                  isErrorShown={touched.bank_id && errors.bank_id}
                >
                  {bankDropdown &&
                    bankDropdown.map((bankName) => (
                      <AdxMenuItem value={bankName.id} selectedvalue={values.bank_id}>
                        {bankName.name}
                      </AdxMenuItem>
                    ))}
                </AdxDropdown>
              </Box>
              <Box width={{ xs: '100%', sm: '40%' }}>
                <AdxInput
                  label="Bank account no."
                  disabled={disable}
                  sxLabel={{
                    color: disable
                      ? 'light-gray-text.main'
                      : 'dark-text.main' /*, fontSize: "30px!important" */,
                  }}
                  sxInput={{
                    color: 'light-gray-text.main',
                    backgroundColor: 'white.main',
                  }}
                  size="standard"
                  placeholder="Bank account no."
                  value={values.bank_acc_number}
                  name="bank_acc_number"
                  onChange={handleChange}
                  errorMessage={<ErrorMessage name="bank_acc_number" component={AdxErrorMessage} />}
                  isErrorShown={touched.bank_acc_number && errors.bank_acc_number}
                />
              </Box>
            </Box>
            {!disable && (
              <Box
                sx={{
                  width: '100%',
                  display: 'flex',
                  flexDirection: 'row',
                  justifyContent: 'flex-end',
                  alignItems: 'center',
                  columnGap: 1,
                }}
              >
                <AdxButton
                  variant={'outlined'}
                  size="small"
                  onClick={() => cancelEditForm(resetForm)}
                  sx={{ width: 0.2 }}
                >
                  Cancel
                </AdxButton>
                {isLoading ? (
                  <AdxButton variant={'contained'} size="small" sx={{ width: 0.2 }}>
                    <CircularProgress color="white" size={20} />
                  </AdxButton>
                ) : (
                  <AdxButton
                    variant={'contained'}
                    size="small"
                    onClick={() => handleCheckRef(handleSubmit)}
                    sx={{ width: 0.2 }}
                  >
                    Save
                  </AdxButton>
                )}
              </Box>
            )}
          </Box>
        </Form>
      )}
    </Formik>
  )
}

export default SalaryDetails
